import { fmt, ddmmyyyy, isZero } from "@/lib/format";
import { COLS } from "@/components/LedgerTable";
import { monthLabel } from "@/pages/SummaryPage";

const SheetTable = ({ title, rows, t, testId }) => (
  <section className="print-avoid-break mb-6" data-testid={testId}>
    <h2 className="text-sm font-bold uppercase tracking-[0.12em] mb-1.5">{title}</h2>
    {rows.length === 0 ? <p className="text-xs text-slate-500">No entries.</p> : (
      <table className="w-full text-[10.5px] border-collapse border border-slate-400">
        <thead>
          <tr className="bg-slate-100">
            {["Date", ...COLS.map((c) => c.l), "Diff", "Note"].map((h) => <th key={h} className="border border-slate-400 px-1.5 py-1 text-left font-bold uppercase">{h}</th>)}
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.id} className={r.is_settlement ? "bg-amber-50" : ""}>
              <td className="border border-slate-300 px-1.5 py-1 font-mono whitespace-nowrap">{ddmmyyyy(r.date)}{r.is_settlement ? " · SETT" : ""}</td>
              {COLS.map((c) => (
                <td key={c.k} className={`border border-slate-300 px-1.5 py-1 text-right font-mono tabular-nums ${c.strong ? "font-semibold" : ""} ${c.k === "pnl" ? (isZero(r.pnl) ? "text-green-700" : "text-red-700 font-semibold") : ""}`}>{fmt(r[c.k])}</td>
              ))}
              <td className="border border-slate-300 px-1.5 py-1 font-mono">{r.diff}</td>
              <td className="border border-slate-300 px-1.5 py-1">{r.note}{r.cashout_note || r.cashout_to ? <span className="text-orange-800"> · Cashout{r.cashout_to ? ` to ${r.cashout_to}` : ""}{r.cashout_note ? `: ${r.cashout_note}` : ""}</span> : ""}{r.reason ? <span className="text-red-700"> — {r.reason}</span> : ""}</td>
            </tr>
          ))}
          <tr className="bg-slate-100 font-bold">
            <td className="border border-slate-400 px-1.5 py-1">TOTAL</td>
            {COLS.map((c) => (
              <td key={c.k} className="border border-slate-400 px-1.5 py-1 text-right font-mono tabular-nums">
                {["deposit", "withdrawal", "cashout", "bonus", "overlimit", "pnl"].includes(c.k) ? fmt(t[c.k]) : ""}
              </td>
            ))}
            <td className="border border-slate-400" colSpan={2} />
          </tr>
        </tbody>
      </table>
    )}
  </section>
);

export const Statement = ({ name, month, daily, sett }) => {
  const t = daily.totals;
  return (
    <>
        <header className="flex items-end justify-between gap-4 border-b-2 border-slate-900 pb-3 mb-5">
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-500">Daily Balance Book · Monthly statement</p>
            <h1 className="text-2xl font-extrabold uppercase tracking-tight" data-testid="print-book-name">{name}</h1>
          </div>
          <div className="text-right">
            <p className="text-lg font-bold" data-testid="print-month">{monthLabel(month)}</p>
            <p className="text-[10px] text-slate-500">Printed {ddmmyyyy(new Date().toISOString().slice(0, 10))}</p>
          </div>
        </header>
        <div className="grid grid-cols-3 sm:grid-cols-6 gap-0 border border-slate-400 mb-6 text-center print-avoid-break" data-testid="print-totals">
          {[["Deposit", t.deposit], ["Withdrawal", t.withdrawal], ["Cashout", t.cashout], ["Bonus", t.bonus], ["Difference", t.pnl], ["Closing Avail", t.closing_available]].map(([l, v]) => (
            <div key={l} className="border-r border-b sm:border-b-0 border-slate-300 last:border-r-0 px-2 py-2">
              <p className="text-[9px] font-semibold uppercase tracking-[0.12em] text-slate-500">{l}</p>
              <p className={`font-mono font-bold text-sm tabular-nums ${l === "Difference" ? (isZero(v) ? "text-green-700" : "text-red-700") : ""}`}>{v == null ? "—" : fmt(v)}</p>
            </div>
          ))}
        </div>
        <SheetTable title="Daily sheet" rows={daily.rows} t={t} testId="print-daily-table" />
        {sett?.rows.length > 0 && <SheetTable title="NEW SETT · Settlement sheet" rows={sett.rows} t={sett.totals} testId="print-sett-table" />}
        <footer className="mt-10 pt-3 border-t border-slate-300 flex justify-between text-[10px] text-slate-500">
          <span>{t.count} entries · {t.mismatches} with Difference ≠ 0</span>
          <span>Checked by: ____________________</span>
        </footer>
    </>
  );
};
